import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { MoreHorizontal, Plus } from "lucide-react";

import { cn } from "@/lib/utils";
import { MOBILE_NAV, NAV_SECTIONS, BOTTOM_ITEMS } from "@/config/navigation";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { QuickCreate } from "@/components/shell/QuickCreate";
import { SHELL } from "@/constants/testIds";

/**
 * Mobile bottom navigation. Home / CRM / Create / Tasks / More.
 * "More" opens a sheet with all remaining approved routes. RESA stays reachable
 * from the persistent top bar on mobile.
 */
export const MobileNav = () => {
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <nav
      data-testid={SHELL.mobileNav}
      className="fixed inset-x-0 bottom-0 z-30 flex items-stretch justify-around border-t border-border bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden"
      aria-label="Mobile"
    >
      {MOBILE_NAV.map((item) => {
        if (item.action === "create") {
          return (
            <QuickCreate
              key={item.key}
              trigger={
                <button data-testid={SHELL.mobileNavItem("create")} className="flex flex-1 flex-col items-center justify-center gap-0.5 py-2" aria-label="Create">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-brand-navy text-white shadow-card">
                    <Plus className="h-5 w-5" />
                  </span>
                </button>
              }
            />
          );
        }
        if (item.action === "more") {
          return (
            <Sheet key={item.key} open={moreOpen} onOpenChange={setMoreOpen}>
              <SheetTrigger asChild>
                <button data-testid={SHELL.mobileMore} className="flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-muted-foreground">
                  <MoreHorizontal className="h-5 w-5" />
                  <span className="text-[10px] font-medium">More</span>
                </button>
              </SheetTrigger>
              <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-2xl">
                <SheetHeader className="text-left">
                  <SheetTitle className="font-brand">Menu</SheetTitle>
                </SheetHeader>
                <div className="mt-4 space-y-5 pb-6">
                  {NAV_SECTIONS.filter((s) => s.id !== "home").map((section) => (
                    <div key={section.id}>
                      <p className="pb-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{section.label}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {section.items.map((it) => {
                          const Icon = it.icon;
                          return (
                            <button
                              key={it.key}
                              data-testid={SHELL.mobileNavItem(it.key)}
                              onClick={() => { setMoreOpen(false); navigate(it.path); }}
                              className="flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2.5 text-left text-sm font-medium text-foreground"
                            >
                              <Icon className="h-4 w-4 text-accent" /> {it.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <div className="grid grid-cols-2 gap-2 border-t border-border pt-4">
                    {BOTTOM_ITEMS.map((it) => {
                      const Icon = it.icon;
                      return (
                        <button key={it.key} onClick={() => { setMoreOpen(false); navigate(it.path); }}
                          className="flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2.5 text-left text-sm font-medium text-foreground">
                          <Icon className="h-4 w-4 text-accent" /> {it.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          );
        }
        const Icon = item.icon;
        return (
          <NavLink
            key={item.key}
            to={item.path}
            end={item.path === "/"}
            data-testid={SHELL.mobileNavItem(item.key)}
            className={({ isActive }) =>
              cn(
                "flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-medium transition-colors",
                isActive ? "text-brand-navy" : "text-muted-foreground",
              )
            }
          >
            <Icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
};
